import { createFileRoute } from '@tanstack/react-router';
import { lazy } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { useAppleRetailAdminDashboard } from '@apple/retail/hooks/useAppleRetailAdminDashboard';
import { SimpleLayout } from '@apple/ui/layouts/simple';
import { TitleLayout } from '@apple/ui/layouts/TitleLayout';

import { requireAuth } from '@apple/features/auth/utils/require-auth';

export const Route = createFileRoute('/_authed/')({
	beforeLoad: args => requireAuth(args),
	component: DashboardRoute,
});

export function DashboardRoute() {
	const { auth } = Route.useRouteContext();
	return auth.hasPermissions(['Manzanita.Security.Features.Snapshot']) ? (
		<AdminDashboardView />
	) : auth.canAccessMultipleStores ? (
		<MultiStoreDashboardView />
	) : (
		<SingleStoreDashboardView />
	);
}

function AdminDashboardView() {
	const { t } = useTranslation('admin-dashboard');
	const { cards } = useAppleRetailAdminDashboard();
	const { auth } = Route.useRouteContext();

	return (
		<TitleLayout fluid title={t('welcome', { name: auth.profile?.firstName })}>
			<LazyAdminDashboard cards={cards} />
		</TitleLayout>
	);
}

export function MultiStoreDashboardView() {
	return (
		<SimpleLayout fluid>
			<LazyMultiStoreDashboard />
		</SimpleLayout>
	);
}

export function SingleStoreDashboardView() {
	const { auth } = Route.useRouteContext();
	const storeCustomerCode = auth.profile!.singleStoreCustomerCode;
	return (
		storeCustomerCode && (
			<SimpleLayout fluid>
				<LazySingleStoreDashboard storeCustomerCode={storeCustomerCode} />
			</SimpleLayout>
		)
	);
}

const LazySingleStoreDashboard = lazy(() =>
	import('@apple/retail/routes/_authed/-dashboard/single-store').then(res => ({
		default: res.SingleStoreDashboard,
	})),
);

const LazyMultiStoreDashboard = lazy(() =>
	import('@apple/retail/routes/_authed/-dashboard/multi-store').then(res => ({
		default: res.MultiStoreDashboard,
	})),
);

const LazyAdminDashboard = lazy(() =>
	import('@apple/retail/routes/_authed/-dashboard/admin').then(res => ({
		default: res.AdminDashboard,
	})),
);
